import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteIcon from '@material-ui/icons/Delete';
import Alert from '@material-ui/lab/Alert';
import HouseholdService from '../../services/household.service';

const useStyles = makeStyles((theme) => ({
  section: { marginTop: theme.spacing(3), maxWidth: 640 },
  row: { display: 'flex', gap: theme.spacing(1), alignItems: 'flex-end', flexWrap: 'wrap' },
  addRow: { display: 'flex', gap: theme.spacing(1), alignItems: 'flex-end', marginTop: theme.spacing(2), flexWrap: 'wrap' },
  you: { color: '#888', marginLeft: 4 },
  note: { color: '#888', marginTop: theme.spacing(1) }
}));

const HouseholdSection = () => {
  const classes = useStyles();
  const [household, setHousehold] = useState(null);
  const [name, setName] = useState('');
  const [addTerm, setAddTerm] = useState('');
  const [message, setMessage] = useState(null); // {severity,text}

  const load = () => {
    HouseholdService.get().then(res => {
      setHousehold(res.data);
      setName(res.data.name);
    }).catch(err => setMessage({ severity: 'error', text: apiErr(err) }));
  };

  useEffect(load, []);

  const apiErr = err => err.response?.data?.message || 'Request failed.';

  const saveName = () => {
    HouseholdService.rename(name)
      .then(() => { setMessage({ severity: 'success', text: 'Household renamed.' }); load(); })
      .catch(err => setMessage({ severity: 'error', text: apiErr(err) }));
  };

  const addMember = () => {
    if (!addTerm.trim()) return;
    HouseholdService.addMember(addTerm.trim(), 'write')
      .then(() => { setMessage({ severity: 'success', text: 'Member added. They must log out and back in to see this household.' }); setAddTerm(''); load(); })
      .catch(err => setMessage({ severity: 'error', text: apiErr(err) }));
  };

  const changeRole = (userId, role) => {
    HouseholdService.changeRole(userId, role)
      .then(() => { setMessage(null); load(); })
      .catch(err => setMessage({ severity: 'error', text: apiErr(err) }));
  };

  const removeMember = (member) => {
    if (!window.confirm(`Remove ${member.username} from this household? They will be moved to a new, empty household of their own.`)) return;
    HouseholdService.removeMember(member.id)
      .then(() => { setMessage({ severity: 'success', text: 'Member removed.' }); load(); })
      .catch(err => setMessage({ severity: 'error', text: apiErr(err) }));
  };

  if (!household) {
    return (
      <div className={classes.section}>
        <Divider />
        <Typography variant="subtitle1" style={{ marginTop: 16 }}>Household</Typography>
        {message && <Alert severity={message.severity}>{message.text}</Alert>}
        {!message && <Typography variant="body2">Loading…</Typography>}
      </div>
    );
  }

  const memberCount = household.members.length;

  return (
    <div className={classes.section}>
      <Divider />
      <Typography variant="subtitle1" style={{ marginTop: 16 }}>Household</Typography>

      {message && <Alert severity={message.severity} style={{ marginBottom: 8 }}>{message.text}</Alert>}

      <div className={classes.row}>
        <TextField label="Household name" value={name} size="small" variant="outlined"
          onChange={e => setName(e.target.value)} />
        <Button color="primary" variant="contained" size="small"
          disabled={!name.trim() || name === household.name} onClick={saveName}>Rename</Button>
      </div>

      <Table size="small" style={{ marginTop: 16 }}>
        <TableHead>
          <TableRow>
            <TableCell>Member</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Role</TableCell>
            <TableCell align="right"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {household.members.map(m => (
            <TableRow key={m.id}>
              <TableCell>{m.username}{m.isSelf && <span className={classes.you}>(you)</span>}</TableCell>
              <TableCell>{m.email}</TableCell>
              <TableCell>
                <Select value={m.household_role || 'write'} onChange={e => changeRole(m.id, e.target.value)}>
                  <MenuItem value="write">write</MenuItem>
                  <MenuItem value="read">read</MenuItem>
                </Select>
              </TableCell>
              <TableCell align="right">
                <Tooltip title={m.isSelf ? "You can't remove yourself" : (memberCount <= 1 ? "Can't remove the last member" : "Remove member")}>
                  <span>
                    <IconButton size="small" disabled={m.isSelf || memberCount <= 1}
                      onClick={() => removeMember(m)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className={classes.addRow}>
        <TextField label="Add member (email or username)" value={addTerm} size="small" variant="outlined"
          style={{ minWidth: 260 }} onChange={e => setAddTerm(e.target.value)} />
        <Button color="primary" variant="outlined" size="small"
          disabled={!addTerm.trim()} onClick={addMember}>Add</Button>
      </div>
      <Typography variant="caption" className={classes.note} component="div">
        Adding an existing account moves it into this household — it will leave its current household
        and see this household's meals and plans instead (after re-logging in).
      </Typography>
    </div>
  );
};

export default HouseholdSection;
